import React, { useState, useEffect, useMemo } from 'react';
import { obtenerPrograma, construirHojas } from '../../servicios/programaService';

const BarraProgreso = ({ pct, color = '#f97316' }) => (
  <div style={{ background: '#f3f4f6', borderRadius: '4px', height: '6px', overflow: 'hidden', marginTop: '8px' }}>
    <div style={{ width: `${Math.min(pct, 100)}%`, background: color, height: '100%', borderRadius: '4px', transition: 'width .4s' }} />
  </div>
);

const KpiItem = ({ label, value, color = '#111827' }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
    <span style={{ fontSize: '12px', color: '#6b7280' }}>{label}</span>
    <span style={{ fontSize: '13px', fontWeight: '700', color }}>{value}</span>
  </div>
);

// Rojo/naranjo/amarillo/verde según el estado de atraso del módulo
function colorSalud(salud) {
  if (salud === 'critico') return '#dc2626';  // rojo — vencido
  if (salud === 'riesgo')  return '#ea580c';  // naranjo — urgente (≤7d)
  if (salud === 'leve')    return '#eab308';  // amarillo — en seguimiento (≤14d)
  return '#16a34a';                            // verde — ok
}

// Rojo/naranjo/amarillo/verde según qué tan atrasado va el Programa de Obra
// (misma escala de brecha usada en MatrizPrograma.js y DashboardPrograma.js)
function colorProgramaBarra(pctGlobal, brecha) {
  if (pctGlobal >= 100) return '#16a34a';  // verde — completado
  if (brecha > 20) return '#dc2626';       // rojo — muy atrasado
  if (brecha > 10) return '#ea580c';       // naranjo — atrasado
  return '#eab308';                        // amarillo — a tiempo / leve atraso
}


const TabResumenProyecto = ({
  tasks = [],
  completedTasks = [],
  materiales = [],
  completedMateriales = [],
  certificados = [],
  fechasRecepcion = {},
  sections = [],
  materialSections = [],
  seccionesRecepcion = [],
  getTopCriticalTasks = () => [],
  getTopCriticalMaterials = () => [],
  onIrAModulo,
}) => {

  // ── Programa de Obra: fetch local ─────────────────────────────────────────
  const [programaDatos, setProgramaDatos] = useState({ programa: null, actividades: [] });
  const [programaCargando, setProgramaCargando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setProgramaCargando(true);
    obtenerPrograma()
      .then(data => { if (!cancelado) setProgramaDatos(data); })
      .catch(() => {})
      .finally(() => { if (!cancelado) setProgramaCargando(false); });
    return () => { cancelado = true; };
  }, []);

  // ── KPIs de módulos ────────────────────────────────────────────────────────
  const subcontratos = useMemo(() => {
    const total = tasks.length + completedTasks.length;
    const completados = completedTasks.length;
    const pct = total > 0 ? Math.round((completados / total) * 100) : 0;
    return { total, activos: tasks.length, completados, pct, secciones: sections.length };
  }, [tasks, completedTasks, sections]);

  const materialesData = useMemo(() => {
    const total = materiales.length + completedMateriales.length;
    const completados = completedMateriales.length;
    const pct = total > 0 ? Math.round((completados / total) * 100) : 0;
    return { total, activos: materiales.length, completados, pct, secciones: materialSections.length };
  }, [materiales, completedMateriales, materialSections]);

  const recepcionData = useMemo(() => {
    let totalDocs = 0, completadosDocs = 0, vencidos = 0, peorDias = null;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    certificados.forEach(cert => {
      (cert.documentos || []).forEach(doc => {
        totalDocs++;
        if (doc.completado) {
          completadosDocs++;
        } else if (doc.fechaEsperada) {
          const dias = Math.round((new Date(doc.fechaEsperada) - hoy) / (1000 * 60 * 60 * 24));
          if (dias < 0) vencidos++;
          if (peorDias === null || dias < peorDias) peorDias = dias;
        }
      });
    });
    const pct = totalDocs > 0 ? Math.round((completadosDocs / totalDocs) * 100) : 0;
    const recepcion = fechasRecepcion?.fechaRecepcionPrograma || null;
    return { totalDocs, completadosDocs, pendientes: totalDocs - completadosDocs, vencidos, peorDias, pct, recepcion, partidas: certificados.length, secciones: seccionesRecepcion.length };
  }, [certificados, fechasRecepcion, seccionesRecepcion]);

  const programaData = useMemo(() => {
    const { programa, actividades = [] } = programaDatos;
    if (!programa) return null;

    const hojas = construirHojas(actividades);
    let sumDur = 0, sumAv = 0;
    for (const a of hojas) {
      const dur = a.duracionBase || 1;
      sumDur += dur; sumAv += (a.porcentajeAvance || 0) * dur;
    }
    const pctGlobal = sumDur > 0 ? Math.round((sumAv / sumDur) * 10) / 10 : 0;

    const fechaSantiago = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
    const hoy = new Date(fechaSantiago + 'T00:00:00');
    const criticas = hojas.filter(a => {
      if ((a.porcentajeAvance ?? 0) >= 100 || !a.fechaFinBase) return false;
      const fecha = new Date(a.fechaFinBase.split('T')[0] + 'T12:00:00');
      return Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24)) <= 7;
    });

    // Avance esperado a hoy: tiempo transcurrido / duración total del proyecto
    let minInicio = Infinity, maxFin = -Infinity;
    for (const a of hojas) {
      if (a.fechaInicioBase) { const t = new Date(a.fechaInicioBase.split('T')[0] + 'T12:00:00').getTime(); if (t < minInicio) minInicio = t; }
      if (a.fechaFinBase)    { const t = new Date(a.fechaFinBase.split('T')[0]    + 'T12:00:00').getTime(); if (t > maxFin)    maxFin = t; }
    }
    const durTotal = (maxFin !== -Infinity && minInicio !== Infinity) ? (maxFin - minInicio) / (1000 * 60 * 60 * 24) : 0;
    const diasTransc = minInicio !== Infinity ? Math.max(0, (hoy.getTime() - minInicio) / (1000 * 60 * 60 * 24)) : 0;
    const pctEsperado = durTotal > 0 ? Math.min(100, Math.round((diasTransc / durTotal) * 100)) : 0;
    const brecha = pctEsperado - pctGlobal;

    return {
      totalHojas: hojas.length,
      completadas: hojas.filter(a => (a.porcentajeAvance ?? 0) >= 100).length,
      criticas: criticas.length,
      pctGlobal,
      pctEsperado,
      brecha,
    };
  }, [programaDatos]);

  // ── Salud por módulo (para colorear % y barras) ───────────────────────────
  const saludSubcontratos = useMemo(() => {
    const items = getTopCriticalTasks();
    if (items.some(t => t.daysUntilDue < 0))   return 'critico';
    if (items.some(t => t.daysUntilDue <= 7))  return 'riesgo';
    if (items.some(t => t.daysUntilDue <= 14)) return 'leve';
    return 'ok';
  }, [getTopCriticalTasks]);

  const saludMateriales = useMemo(() => {
    const items = getTopCriticalMaterials();
    if (items.some(m => m.daysUntilDue < 0))   return 'critico';
    if (items.some(m => m.daysUntilDue <= 7))  return 'riesgo';
    if (items.some(m => m.daysUntilDue <= 14)) return 'leve';
    return 'ok';
  }, [getTopCriticalMaterials]);

  const saludRecepcion = useMemo(() => {
    const { vencidos, pct, peorDias } = recepcionData;
    if (vencidos > 0)   return 'critico';
    if (pct === 100)     return 'ok';
    if (peorDias !== null && peorDias <= 7)  return 'riesgo';
    if (peorDias !== null && peorDias <= 14) return 'leve';
    return 'ok';
  }, [recepcionData]);

  const fmt = (fecha) => {
    if (!fecha) return '—';
    return new Date(fecha).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="resumen-proyecto-contenedor">
      <div className="resumen-proyecto-header">
        <h2 className="resumen-proyecto-titulo">Resumen del Proyecto</h2>
        <p className="resumen-proyecto-subtitulo">Vista general de todos los módulos</p>
      </div>

      {/* ── CARDS DE MÓDULOS ─────────────────────────────────────────────── */}
      <div className="resumen-proyecto-grid">

        {/* CARD SUBCONTRATOS */}
        <div className="resumen-card">
          <div className="resumen-card-header">
            <span className="resumen-card-badge resumen-card-badge-naranja">SUBCONTRATOS</span>
            <span className="resumen-card-pct" style={{ color: colorSalud(saludSubcontratos) }}>
              {subcontratos.pct}%
            </span>
          </div>
          <BarraProgreso pct={subcontratos.pct} color={colorSalud(saludSubcontratos)} />
          <div className="resumen-card-kpis">
            <KpiItem label="Total subcontratos" value={subcontratos.total} />
            <KpiItem label="Completadas" value={subcontratos.completados} color="#16a34a" />
            <KpiItem label="En curso" value={subcontratos.activos} color="#f97316" />
            <KpiItem label="Secciones" value={subcontratos.secciones} />
          </div>
          <button className="resumen-card-btn" onClick={() => onIrAModulo && onIrAModulo('gestracoo')}>
            Ver Subcontratos →
          </button>
        </div>

        {/* CARD MATERIALES */}
        <div className="resumen-card">
          <div className="resumen-card-header">
            <span className="resumen-card-badge resumen-card-badge-naranja">MATERIALES</span>
            <span className="resumen-card-pct" style={{ color: colorSalud(saludMateriales) }}>
              {materialesData.pct}%
            </span>
          </div>
          <BarraProgreso pct={materialesData.pct} color={colorSalud(saludMateriales)} />
          <div className="resumen-card-kpis">
            <KpiItem label="Total materiales" value={materialesData.total} />
            <KpiItem label="Completados" value={materialesData.completados} color="#16a34a" />
            <KpiItem label="En curso" value={materialesData.activos} color="#f97316" />
            <KpiItem label="Secciones" value={materialesData.secciones} />
          </div>
          <button className="resumen-card-btn" onClick={() => onIrAModulo && onIrAModulo('gestracoo')}>
            Ver Materiales →
          </button>
        </div>

        {/* CARD RECEPCIÓN */}
        <div className="resumen-card">
          <div className="resumen-card-header">
            <span className="resumen-card-badge resumen-card-badge-naranja">RECEPCIÓN</span>
            <span className="resumen-card-pct" style={{ color: colorSalud(saludRecepcion) }}>
              {recepcionData.pct}%
            </span>
          </div>
          <BarraProgreso pct={recepcionData.pct} color={colorSalud(saludRecepcion)} />
          <div className="resumen-card-kpis">
            <KpiItem label="Total documentos" value={recepcionData.totalDocs} />
            <KpiItem label="Completados" value={recepcionData.completadosDocs} color="#16a34a" />
            <KpiItem label="Pendientes" value={recepcionData.pendientes} color="#f97316" />
            {recepcionData.vencidos > 0 && (
              <KpiItem label="Vencidos" value={recepcionData.vencidos} color="#dc2626" />
            )}
            <KpiItem label="Partidas" value={recepcionData.partidas} />
            <KpiItem label="Recepción programada" value={fmt(recepcionData.recepcion)} />
          </div>
          <button className="resumen-card-btn" onClick={() => onIrAModulo && onIrAModulo('recepcion')}>
            Ver Recepción →
          </button>
        </div>

        {/* CARD PROGRAMA DE OBRA */}
        <div className="resumen-card">
          <div className="resumen-card-header">
            <span className="resumen-card-badge resumen-card-badge-naranja">PROGRAMA DE OBRA</span>
            {programaCargando ? (
              <span className="resumen-card-pct" style={{ fontSize: '13px', color: '#9ca3af' }}>…</span>
            ) : programaData ? (
              <span className="resumen-card-pct" style={{ color: colorProgramaBarra(programaData.pctGlobal, programaData.brecha) }}>
                {programaData.pctGlobal}%
              </span>
            ) : (
              <span className="resumen-card-pct" style={{ fontSize: '13px', color: '#9ca3af' }}>—</span>
            )}
          </div>
          {programaCargando ? (
            <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center', padding: '12px 0' }}>Cargando…</div>
          ) : programaData ? (
            <>
              <BarraProgreso pct={programaData.pctGlobal} color={colorProgramaBarra(programaData.pctGlobal, programaData.brecha)} />
              <div className="resumen-card-kpis">
                <KpiItem label="Total partidas" value={programaData.totalHojas} />
                <KpiItem label="Completadas" value={programaData.completadas} color="#16a34a" />
                <KpiItem label="Críticas (≤7 días o vencidas)" value={programaData.criticas}
                  color={programaData.criticas > 0 ? '#dc2626' : '#6b7280'} />
              </div>
            </>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center', padding: '12px 0', flex: 1 }}>
              Sin programa importado
            </div>
          )}
          <button className="resumen-card-btn" onClick={() => onIrAModulo && onIrAModulo('programa')}>
            Ver Programa →
          </button>
        </div>

      </div>
    </div>
  );
};

export default TabResumenProyecto;
